import {Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {ToastrService} from 'ngx-toastr';
import {finalize} from 'rxjs/operators';
import {
    EventService,
    EventBookingDTO,
    EventDTO,
    EventFormAnswer,
    EventTicketOption,
    RegistrationChangeDTO,
    RegistrationConfirmRequest,
    RegistrationEditRequest
} from '../api/event-api.service';
import {SpinnerService} from '../shared/services/spinner.service';
import {missingRequiredLabels, pruneAnswers} from '../registration-questions/registration-questions.util';

/**
 * Edit an existing registration — CONFERENCE (ticket options) or LOCAL (adults / children / seniors) — and, for
 * organizers, record a payment against it. Used by the registrant on their own confirmed registration (no reason,
 * no payment section) and by the committee / chapter executive / NEC on anyone's.
 *
 * The amount shown is an estimate (current prices); the server prices the change, locks earlier prices and the
 * early-bird discount, and returns the authoritative amount due / paid / balance.
 */
@Component({
    selector: 'app-registration-edit-dialog',
    templateUrl: './registration-edit-dialog.component.html',
    styleUrls: ['./registration-edit-dialog.component.css']
})
export class RegistrationEditDialogComponent implements OnInit {

    @Input() event!: EventDTO;
    /** A booking (CONFERENCE) or a registration row (LOCAL): either way it carries its booking id. */
    @Input() registration!: any;
    @Input() mode: 'organizer' | 'member' = 'organizer';
    /** Chapter executive committee / NEC may waive a remaining balance; event-committee members may not. */
    @Input() canWaive = false;
    @Input() registrantName = '';
    @Output() saved = new EventEmitter<EventBookingDTO>();
    @Output() closed = new EventEmitter<void>();

    quantities: { [optionId: string]: number } = {};
    adults = 0;
    children = 0;
    seniors = 0;
    childAges = '';
    donation = 0;
    attending: '' | 'YES' | 'NO' = '';
    answers: EventFormAnswer[] = [];
    reason = '';

    recordPayment = false;
    paymentAmount: number | null = null;
    paymentMethod = 'zelle';
    paymentReference = '';
    waiveBalance = false;
    private amountEdited = false;

    history: RegistrationChangeDTO[] = [];
    historyLoaded = false;
    showHistory = false;
    submitAttempted = false;
    saving = false;
    error = '';

    private original = {quantities: {} as { [id: string]: number }, adults: 0, children: 0, seniors: 0, donation: 0};

    constructor(
        private eventApi: EventService,
        private toastr: ToastrService,
        private spinner: SpinnerService
    ) {
    }

    ngOnInit(): void {
        const r = this.registration || {};
        (r.selectedOptions || []).forEach((o: any) => {
            if (o.quantity > 0) this.quantities[o.optionId] = o.quantity;
        });
        this.adults = Number(r.numAdults ?? r.adults ?? 0) || 0;
        this.children = Number(r.numChildren ?? r.children ?? 0) || 0;
        this.seniors = Number(r.numSeniors ?? r.seniors ?? 0) || 0;
        this.childAges = r.childAges || '';
        this.donation = Number(r.donationAmt ?? 0) || 0;
        this.attending = (r.attending as any) || '';
        this.answers = r.formAnswers ? JSON.parse(JSON.stringify(r.formAnswers)) : [];
        this.paymentMethod = this.normalizeMethod(r.paymentType);
        this.original = {
            quantities: {...this.quantities}, adults: this.adults, children: this.children,
            seniors: this.seniors, donation: this.donation
        };
    }

    // ---- what kind of event / what to show ----

    get isConference(): boolean {
        return this.event?.eventCategory === 'CONFERENCE';
    }

    get isOrganizer(): boolean {
        return this.mode === 'organizer';
    }

    get bookingId(): string {
        return this.registration?.id || this.registration?.bookingId || '';
    }

    get questions() {
        return this.event?.registrationQuestions || [];
    }

    get showAttendingError(): boolean {
        return this.submitAttempted && this.usesLegacyAttending && !this.attending && !this.isOrganizer;
    }

    get usesLegacyAttending(): boolean {
        return this.isConference && this.event?.registrationQuestions == null;
    }

    get ticketOptions(): EventTicketOption[] {
        return this.event?.ticketOptions || [];
    }

    get isPaidLocal(): boolean {
        return String(this.event?.eventType || '').toLowerCase() === 'paid';
    }

    get isConfirmed(): boolean {
        return this.registration?.status === 'PAYMENT_CONFIRMED';
    }

    // ---- money ----

    get amountDue(): number {
        return Number(this.registration?.amountDue ?? this.registration?.totalAmount ?? 0) || 0;
    }

    get amountPaid(): number {
        const paid = this.registration?.amountPaid;
        if (paid != null) return Number(paid);
        return this.isConfirmed ? this.amountDue : 0;
    }

    /** Current prices applied to what changed, on top of what was owed. Early-bird is judged by the server. */
    get estimatedTotal(): number {
        let delta = 0;
        if (this.isConference) {
            const ids = new Set([...Object.keys(this.original.quantities), ...Object.keys(this.quantities)]);
            ids.forEach(id => {
                const option = this.ticketOptions.find(o => o.optionId === id);
                if (!option || option.paid === false) return;
                const price = parseFloat(option.price) || 0;
                delta += ((this.quantities[id] || 0) - (this.original.quantities[id] || 0)) * price;
            });
        } else if (this.isPaidLocal) {
            delta += (this.adults - this.original.adults) * (parseFloat(this.event.eventFeeAdult || '0') || 0);
            delta += (this.children - this.original.children) * (parseFloat(this.event.eventFeeChild || '0') || 0);
            delta += (this.seniors - this.original.seniors) * (parseFloat(this.event.eventFeeSeniorCitizen || '0') || 0);
        }
        delta += (Number(this.donation) || 0) - this.original.donation;
        return Math.max(0, Math.round((this.amountDue + delta) * 100) / 100);
    }

    get estimatedBalance(): number {
        return Math.round((this.estimatedTotal - this.amountPaid) * 100) / 100;
    }

    get changed(): boolean {
        if (this.isConference) {
            const ids = new Set([...Object.keys(this.original.quantities), ...Object.keys(this.quantities)]);
            for (const id of ids) {
                if ((this.quantities[id] || 0) !== (this.original.quantities[id] || 0)) return true;
            }
        } else if (this.adults !== this.original.adults || this.children !== this.original.children
            || this.seniors !== this.original.seniors) {
            return true;
        }
        return (Number(this.donation) || 0) !== this.original.donation
            || this.answersChanged || this.attending !== ((this.registration?.attending as any) || '')
            || (!this.isConference && this.childAges !== (this.registration?.childAges || ''));
    }

    private get answersChanged(): boolean {
        return JSON.stringify(this.answers || []) !== JSON.stringify(this.registration?.formAnswers || []);
    }

    /** The default amount to record: whatever is outstanding after this edit (never negative). */
    get suggestedPayment(): number {
        return Math.max(0, this.estimatedBalance);
    }

    /** True when, after this edit, the registrant has paid more than they owe: record a refund, not a payment. */
    get isRefund(): boolean {
        return this.estimatedBalance < -0.004;
    }

    /** Amount of the overpayment, which is the default refund. */
    get suggestedRefund(): number {
        return Math.max(0, Math.round(-this.estimatedBalance * 100) / 100);
    }

    onToggleRecordPayment(): void {
        if (this.recordPayment) {
            this.paymentAmount = this.isRefund ? this.suggestedRefund : this.suggestedPayment;
        }
    }

    /** The overpayment changes as quantities are edited; keep an untouched default in step with it. */
    onQuantityChanged(): void {
        if (this.recordPayment && !this.amountEdited) {
            this.paymentAmount = this.isRefund ? this.suggestedRefund : this.suggestedPayment;
        }
    }

    // ---- actions ----

    save(): void {
        this.error = '';
        this.submitAttempted = true;
        if (this.isOrganizer && !this.reason.trim()) {
            this.error = 'Please give a reason for the change.';
            return;
        }
        if (!this.isConference && this.adults + this.children + this.seniors < 1) {
            this.error = 'A registration needs at least one attendee.';
            return;
        }
        if (this.isConference && this.ticketOptions.some(o => o.paid !== false)
            && !Object.entries(this.quantities).some(([id, q]) => q > 0 && this.ticketOptions.find(o => o.optionId === id)?.paid !== false)) {
            this.error = 'Please select at least one ticket option.';
            return;
        }
        // The built-in "Are you attending?" question is required of the registrant themself; an organizer fixing
        // someone else's older registration may leave it unanswered.
        if (this.usesLegacyAttending && !this.attending && !this.isOrganizer) {
            this.error = 'Please select whether you are attending the conference.';
            return;
        }
        const missing = missingRequiredLabels(this.questions, this.answers);
        if (this.questions.length > 0 && missing.length > 0) {
            this.error = `Please answer: ${missing.join(', ')}`;
            return;
        }
        if (this.recordPayment && this.isOrganizer) {
            if (this.paymentAmount == null || this.paymentAmount < 0 || (this.isRefund && this.paymentAmount <= 0)) {
                this.error = this.isRefund ? 'Enter the amount refunded.' : 'Enter the amount received.';
                return;
            }
            if (this.isRefund && !this.canWaive) {
                this.error = 'Only the chapter executive committee or NEC can record a refund.';
                return;
            }
        }
        if (!this.changed && !(this.recordPayment && this.isOrganizer)) {
            this.error = 'Nothing has changed.';
            return;
        }

        const request: RegistrationEditRequest = {donationAmt: Number(this.donation) || 0, reason: this.reason.trim() || undefined};
        if (this.isConference) {
            request.selectedOptions = Object.entries(this.quantities)
                .filter(([, q]) => q > 0).map(([optionId, quantity]) => ({optionId, quantity}));
            if (this.usesLegacyAttending && this.attending) request.attending = this.attending;
        } else {
            request.numAdults = this.adults;
            request.numChildren = this.children;
            request.numSeniors = this.seniors;
            request.childAges = this.childAges;
        }
        if (this.questions.length > 0) request.formAnswers = pruneAnswers(this.questions, this.answers);

        this.saving = true;
        this.spinner.show();
        const afterEdit = (booking: EventBookingDTO) => {
            if (this.recordPayment && this.isOrganizer) {
                const confirm: RegistrationConfirmRequest = {
                    amount: Number(this.paymentAmount) || 0,
                    method: this.paymentMethod,
                    reference: this.paymentReference.trim() || undefined,
                    waiveBalance: this.isRefund ? undefined : (this.waiveBalance || undefined),
                    refund: this.isRefund || undefined,
                    note: this.reason.trim() || undefined
                };
                this.eventApi.confirmRegistration(this.event.id!, this.bookingId, confirm)
                    .pipe(finalize(() => this.done()))
                    .subscribe({
                        next: (confirmed) => {
                            this.toastr.success(this.isRefund ? 'Registration updated and refund recorded.'
                                : 'Registration updated and payment recorded.');
                            this.saved.emit(confirmed);
                        },
                        error: (err) => {
                            // The edit itself is saved; say so, and let them retry the payment.
                            this.error = 'The changes were saved, but the payment was not recorded: ' + this.messageOf(err);
                            this.saved.emit(booking);
                        }
                    });
            } else {
                this.done();
                this.toastr.success(this.balanceSentence(booking) || 'Registration updated.');
                this.saved.emit(booking);
            }
        };

        if (this.changed) {
            this.eventApi.editRegistration(this.event.id!, this.bookingId, request).subscribe({
                next: afterEdit,
                error: (err) => {
                    this.done();
                    this.error = this.messageOf(err);
                }
            });
        } else {
            afterEdit(this.registration as EventBookingDTO);
        }
    }

    /** Organizers: send a confirmed registration back to Payment Pending so it can be changed, then confirmed again. */
    reopen(): void {
        this.error = '';
        if (!this.reason.trim()) {
            this.submitAttempted = true;
            this.error = 'Please give a reason for the change.';
            return;
        }
        this.saving = true;
        this.spinner.show();
        this.eventApi.reopenRegistration(this.event.id!, this.bookingId, {reason: this.reason.trim()})
            .pipe(finalize(() => this.done()))
            .subscribe({
                next: (booking) => {
                    this.toastr.success('Moved back to Payment Pending. Make your changes, then confirm the payment again.');
                    this.saved.emit(booking);
                },
                error: (err) => this.error = this.messageOf(err)
            });
    }

    toggleHistory(): void {
        this.showHistory = !this.showHistory;
        if (this.showHistory && !this.historyLoaded) {
            this.eventApi.getRegistrationHistory(this.event.id!, this.bookingId).subscribe({
                next: (rows) => {
                    this.history = rows;
                    this.historyLoaded = true;
                },
                error: () => {
                    this.historyLoaded = true;
                    this.toastr.error('Could not load the history');
                }
            });
        }
    }

    close(): void {
        if (!this.saving) this.closed.emit();
    }

    kindLabel(kind?: string): string {
        switch (kind) {
            case 'MEMBER': return 'Registrant';
            case 'COMMITTEE': return 'Event committee';
            case 'CHAPTER_EXEC': return 'Chapter executive';
            case 'NEC': return 'NEC';
            default: return '';
        }
    }

    trackById(_i: number, o: EventTicketOption): string | undefined {
        return o.optionId;
    }

    setQuantity(optionId: string, value: any): void {
        const n = Math.max(0, Math.floor(Number(value) || 0));
        if (n > 0) this.quantities[optionId] = n; else delete this.quantities[optionId];
        this.onQuantityChanged();
    }

    markAmountEdited(): void {
        this.amountEdited = true;
    }

    private balanceSentence(b: EventBookingDTO): string {
        if (b.balance != null && b.balance > 0.004) return `Updated. Balance due: $${b.balance.toFixed(2)}.`;
        if (b.balance != null && b.balance < -0.004) return `Updated. Overpaid by $${(-b.balance).toFixed(2)}; the organizers will arrange a refund.`;
        return '';
    }

    private normalizeMethod(type?: string): string {
        const t = String(type || '').toLowerCase();
        return ['zelle', 'check', 'cash', 'stripe'].includes(t) ? t : 'zelle';
    }

    private messageOf(err: any): string {
        if (err?.status === 0) return 'Could not reach the server. Please try again in a moment.';
        return err?.error?.message || err?.error?.error || err?.message || 'The change could not be saved.';
    }

    private done(): void {
        this.saving = false;
        this.spinner.hide();
    }
}
